import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Descriptions, Space, Spin, Tag, Typography } from 'antd'
import adminAxios from './adminAxios'
import { API_SESSION_EXPIRED_EVENT } from '../utils/apiAuthRecovery'

const { Paragraph, Text, Title } = Typography
const ENDPOINT = '/api/admin/support/intake-prerequisites'
const WORKSPACE = 'scribeshadow-business'
const MAILBOX = 'contact@scribeshadow.com'
const STATUSES = ['available', 'missing', 'stale', 'ambiguous', 'unauthorized', 'invalid', 'review_required'] as const
type Status = typeof STATUSES[number]
type Field<T> = { value: T | null; status: Status; provenance: string; asOf: string | null }
export type IntakePrerequisites = {
  schemaVersion: 1
  workspaceId: string
  mailbox: string
  observedAt: string
  status: Status
  connectionId: Field<string>
  sourcePolicyVersion: Field<number>
  cursor: Field<string>
  runtimeReadiness: 'not_checked'
}
const isTime = (value: unknown): value is string =>
  typeof value === 'string' && value.length <= 40 && Number.isFinite(Date.parse(value))
const isStatus = (value: unknown): value is Status => STATUSES.includes(value as Status)
const isStringValue = (value: unknown): value is string => typeof value === 'string' && value.length > 0 && value.length <= 512
const isVersion = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 1
const isField = <T,>(field: any, isValue: (value: unknown) => value is T): field is Field<T> =>
  Boolean(field && isStatus(field.status) && (field.value === null || isValue(field.value))
    && (field.status !== 'available' || field.value !== null)
    && typeof field.provenance === 'string' && field.provenance.length > 0 && field.provenance.length <= 512
    && (field.asOf === null || isTime(field.asOf)))

export const isIntakePrerequisites = (value: any): value is IntakePrerequisites =>
  Boolean(value && value.schemaVersion === 1 && value.workspaceId === WORKSPACE && value.mailbox === MAILBOX
    && isTime(value.observedAt) && isStatus(value.status) && value.runtimeReadiness === 'not_checked'
    && isField(value.connectionId, isStringValue) && isField(value.sourcePolicyVersion, isVersion)
    && isField(value.cursor, isStringValue))

const fieldStatus = (status: Status) => status.replace('_', ' ')
const statusColor = (status: Status) => status === 'available' ? 'green' : status === 'unauthorized' || status === 'invalid' ? 'red' : 'orange'
const errorMessage = (error: any): string => {
  switch (error?.response?.status) {
    case 401: return 'Sign in again to read intake prerequisites.'
    case 403: return 'Unauthorized: the signed-in account does not have the existing owner access required for this mailbox.'
    case 404: return 'Unavailable: the prerequisite view or existing agent platform is not enabled on this API.'
    case 503: return 'The prerequisite records could not be read. Refresh to try again.'
    default: return 'The prerequisite view could not be loaded. Refresh to try again.'
  }
}

const FieldValue = <T extends string | number,>({ field }: { field: Field<T> }) => (
    <Space direction="vertical" size="small">
      <Tag color={statusColor(field.status)}>{fieldStatus(field.status)}</Tag>
      <Text code>{field.value === null ? 'No authorized value available' : String(field.value)}</Text>
      <Text type="secondary">Source: {field.provenance}</Text>
      <Text type="secondary">Record as of: {field.asOf || 'Not recorded'}</Text>
    </Space>
)

const SupportIntakePrerequisites: React.FC = () => {
  const [data, setData] = useState<IntakePrerequisites | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const generation = useRef(0)
  const load = useCallback(async () => {
    const current = ++generation.current
    setData(null)
    setError(null)
    setLoading(true)
    try {
      const response = await adminAxios.get(ENDPOINT, { timeout: 15000 })
      if (current !== generation.current) return
      if (!isIntakePrerequisites(response.data)) {
        setError('Invalid prerequisite response. No values are displayed; request a reviewed API check.')
        return
      }
      setData(response.data)
    } catch (failure) {
      if (current === generation.current) setError(errorMessage(failure))
    } finally {
      if (current === generation.current) setLoading(false)
    }
  }, [])
  useEffect(() => {
    void load()
    const expire = () => {
      generation.current += 1
      setData(null)
      setLoading(false)
      setError('Sign in again to read intake prerequisites.')
    }
    window.addEventListener(API_SESSION_EXPIRED_EVENT, expire)
    return () => {
      generation.current += 1
      window.removeEventListener(API_SESSION_EXPIRED_EVENT, expire)
    }
  }, [load])

  return (
    <section aria-labelledby="support-intake-prerequisites-title">
      <Title level={3} id="support-intake-prerequisites-title">Intake prerequisites</Title>
      <Paragraph>Read existing records for {MAILBOX} in {WORKSPACE}. This view does not change settings, consent, or mailbox history.</Paragraph>
      <Button onClick={() => void load()} disabled={loading}>Refresh prerequisites</Button>
      <div aria-live="polite" aria-busy={loading}>
        {loading && <Space><Spin size="small" /><Text>Reading intake prerequisites</Text></Space>}
        {error && <Alert type="error" showIcon message={error} />}
        {data && <>
          <Paragraph>Stored records: <Tag color={statusColor(data.status)}>{fieldStatus(data.status)}</Tag></Paragraph>
          <Paragraph>Observed at: {data.observedAt}</Paragraph>
          <Alert type="info" showIcon message="Runtime readiness has not been checked."
            description="Available means stored values were read. It does not confirm Gmail history retention, current consent, or permission to start intake. Complete the reviewed owner workflow and bounded check before activation." />
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Connection ID"><FieldValue field={data.connectionId} /></Descriptions.Item>
            <Descriptions.Item label="Source-policy version"><FieldValue field={data.sourcePolicyVersion} /></Descriptions.Item>
            <Descriptions.Item label="Authorized mailbox cursor"><FieldValue field={data.cursor} /></Descriptions.Item>
          </Descriptions>
          <Paragraph type="secondary">Cursor freshness uses the stored sync time (24-hour warning threshold). Missing or future sync times are stale. This does not establish whether Gmail will still accept the history cursor.</Paragraph>
          {data.status !== 'available' && <Alert type="warning" showIcon
            message="Activation prerequisites need review."
            description="Do not guess missing values or replace a stale or unauthorized cursor. Follow the existing owner setup and reviewed recovery process." />}
        </>}
      </div>
    </section>
  )
}

export default SupportIntakePrerequisites
